import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { cloneDeep, defer, delay, filter, range, uniq } from 'lodash-es';
import { catchError, Observable, of, Subscription } from 'rxjs';
import { Ref } from '../../model/ref';
import { ActionService } from '../../service/action.service';
import { Store } from '../../store/store';
import { hasTag } from '../../util/tag';

export type Piece = 'r' | 'b';
export type Spot = {
  index: number,
  col: number,
  red: boolean,
  move?: boolean,
  top: boolean
  pieces: Piece[],
};

// Game state type - represents the complete backgammon game state
type GameState = {
  board: string[];
  moves: number[][];
  turn?: Piece;
  bar: Piece[];
  spots: Spot[];
  redOff: Piece[];
  blackOff: Piece[];
  redDice: number[];
  blackDice: number[];
  diceUsed: number[];
  winner?: Piece;
  lastMovedSpots: Record<number, number>;
  lastMovedOff: Record<Piece, number>;
};

type AnimationState = {
  rollingPiece?: Piece;
  from?: number;
  to?: number;
  piece?: Piece;
  fromStackIndex?: number;
  toStackIndex?: number;
  pre: GameState;
  post: GameState;
};

function createInitialGameState(): GameState {
  const state: GameState = {
    bar: [],
    blackDice: [],
    blackOff: [],
    board: [],
    diceUsed: [],
    lastMovedOff: { 'r': 0, 'b': 0 },
    lastMovedSpots: {},
    moves: [],
    redDice: [],
    redOff: [],
    spots: range(24).map(index => (<Spot>{
      index,
      col: index < 12 ? index + 1 : 24 - index,
      red: !(index % 2),
      top: index < 12,
      pieces: [] as Piece[],
    })),
  };
  state.spots[ 0].pieces = [...'rr'] as Piece[];
  state.spots[ 5].pieces = [...'bbbbb'] as Piece[];
  state.spots[ 7].pieces = [...'bbb'] as Piece[];
  state.spots[11].pieces = [...'rrrrr'] as Piece[];
  state.spots[12].pieces = [...'bbbbb'] as Piece[];
  state.spots[16].pieces = [...'rrr'] as Piece[];
  state.spots[18].pieces = [...'rrrrr'] as Piece[];
  state.spots[23].pieces = [...'bb'] as Piece[];
  return state;
}

function getAnimation(state: GameState, update: string): AnimationState | null {
  if (update.includes('-')) {
    // Parse roll
    const parts = update.split(/[\s/*()]+/g).filter(p => !!p);
    const p = parts[0] as Piece;
    const d1 = parseInt(update[2]);
    const d2 = parseInt(update[4]);
    const newState = applyRoll(state, p, d1, d2);
    return {
      pre: state,
      post: newState,
      rollingPiece: p,
    };
  }
  if (update.includes('/')) {
    // Parse the move
    const parts = update.split(/[\s/*()]+/g).filter(p => !!p);
    const p = parts[0] as Piece;
    const bar = update.includes('bar');
    const off = update.includes('off');
    const from = bar ? -1 : parseInt(parts[1]) - 1;
    const to = off ? -2 : parseInt(parts[2]) - 1;

    // Track the source stack position of the moving piece
    let fromStackIndex = -1;
    if (from >= 0 && from < 24) {
      const sourceSpot = state.spots[from];
      fromStackIndex = sourceSpot.pieces.filter(piece => piece === p).length - 1;
    } else if (from === -1) {
      const sourceBar = p === 'r' ? getRedBar(state) : getBlackBar(state);
      fromStackIndex = sourceBar.length - 1;
    }

    // Use pure function to compute new state
    const newState = applyMove(state, p, from, to);

    // Compute destination stack index from post-move state
    let toStackIndex = -1;
    if (to >= 0 && to < 24) {
      const destSpot = newState.spots[to];
      toStackIndex = destSpot.pieces.filter(piece => piece === p).length - 1;
    } else if (to === -1) {
      const destBar = p === 'r' ? getRedBar(newState) : getBlackBar(newState);
      toStackIndex = destBar.length - 1;
    } else if (to === -2) {
      const destOff = p === 'r' ? newState.redOff : newState.blackOff;
      toStackIndex = destOff.length - 1;
    }
    return {
      pre: state,
      post: newState,
      from,
      to,
      piece: p,
      fromStackIndex,
      toStackIndex,
    };
  }
  console.log('unknown update:', update);
  return null;
}

function renderMove(p: Piece, from: number, to: number, hit?: boolean) {
  return p + ' ' + (from < 0 ? 'bar' : from + 1) + '/' + (to < 0 ? 'off' : to + 1) + (hit ? '*' : '');
}

function getRedPips(state: GameState): number {
  return state.spots.flatMap(s => s.pieces.filter(p => p === 'r')).length;
}

function getBlackPips(state: GameState): number {
  return state.spots.flatMap(s => s.pieces.filter(p => p === 'b')).length;
}

function getRedBar(state: GameState): Piece[] {
  return state.bar.filter(b => b === 'r');
}

function getBlackBar(state: GameState): Piece[] {
  return state.bar.filter(b => b === 'b');
}

function isClosed(state: GameState): boolean {
  if (state.turn === 'r') {
    for (let i = 0; i < 18; i++) if (state.spots[i].pieces[0] === 'r') return false;
    return true;
  } else if (state.turn === 'b') {
    for (let i = 6; i < 24; i++) if (state.spots[i].pieces[0] === 'b') return false;
    return true;
  }
  return false;
}

function isInRedHome(state: GameState): boolean {
  for (let i = 18; i < 24; i++) if (state.spots[i].pieces[0] === 'b') return true;
  return false;
}

function isInBlackHome(state: GameState): boolean {
  for (let i = 0; i < 7; i++) if (state.spots[i].pieces[0] === 'r') return true;
  return false;
}

function getDice(state: GameState, turn: Piece): number[] {
  let result = filter([...state.redDice, ...state.blackDice], d => !!d);
  if (result.length !== 2) {
    // You can use dice shown if you win the initial roll
    result = [...(turn === 'r' ? state.redDice : state.blackDice)];
  }
  if (result[0] === result[1]) {
    // Doubles
    result.push(...result);
  }
  for (const u of state.diceUsed) {
    result.splice(result.indexOf(u), 1);
  }
  return result;
}

function areDoubles(state: GameState): boolean {
  const ds = state.turn === 'r' ? state.redDice : state.blackDice;
  return ds[0] === ds[1];
}

function isFirstRoll(state: GameState): boolean {
  if (state.diceUsed.length) return false;
  return !state.redDice[1] && !state.blackDice[1];
}

// Get valid moves for a piece at a given index
function getPieceMoves(state: GameState, p: Piece, index: number, ds: number[]): number[] {
  if (!ds?.length) return [];
  const bar = state.bar;
  const spots = state.spots;

  if (index != -1 && bar.find(o => o === p)) return [];
  if (index === -1) {
    const off = bar.filter(o => o === p);
    if (!off.length) return [];
  }
  if (index === -1 && p === 'b') {
    index = 24;
  }
  const result: number[] = [];
  const np = p === 'r' ? 'b' : 'r';
  for (const d of ds) {
    const i = p === 'r' ? index + d : index - d;
    if (i < 0 || i > 23) continue;
    if (spots[i].pieces.length > 1 && spots[i].pieces[0] === np) continue;
    const rest = [...ds];
    rest.splice(ds.indexOf(d), 1);
    result.push(i, ...getPieceMoves(state, p, i, rest));
  }
  if (isClosed(state)) {
    const needDice = p === 'r' ? 24 - index : index + 1;
    if (ds.find(d => d === needDice)) {
      result.push(-2);
    } else {
      for (let i = 6; i > needDice; i--) {
        const spot = spots[p === 'r' ? 24 - i : i - 1];
        for (const piece of spot.pieces) {
          if (piece !== state.turn) break;
          if (!ds.find(d => d >= i)) return uniq(result);
        }
      }
      if (ds.find(d => d >= needDice)) {
        result.push(-2);
      }
    }
  }
  return uniq(result);
}

// Get all valid moves for the current player
function getAllMoves(state: GameState): number[][] {
  if (!state.turn) return [];
  const result: number[][] = [];
  const ds = getDice(state, state.turn);
  const bar = getPieceMoves(state, state.turn, -1, ds);
  if (bar.length) {
    result.length = 1;
    result[-1] = bar;
    return result;
  }
  for (let i = 0; i < 24; i++) {
    const p = state.spots[i].pieces[0];
    if (p !== state.turn) continue;
    const ms = getPieceMoves(state, p, i, ds);
    if (ms.length) result[i] = ms;
  }
  return result;
}

// Get which dice are used for a specific move
function getDiceUsed(state: GameState, p: Piece, from: number, to: number, ds: number[]): number[] {
  if (from === -1 && p === 'b') {
    from = 24;
  }
  if (to === -2) {
    const d = p === 'r' ? 24 - from : from + 1;
    if (ds.includes(d)) return [d];
    const u: number[] = [];
    let v = 0;
    for (const i of ds) {
      if (i > d) continue;
      v += i;
      u.push(i);
      if (v >= d) return u;
    }
    u.length = 0;
    v = 0;
    for (const i of ds) {
      v += i;
      u.push(i);
      if (v >= d) return u;
    }
    throw $localize`Illegal move ${renderMove(p, from, to)}`;
  }
  if (areDoubles(state)) {
    const result: number[] = [];
    if (to < 0) {
      to = p === 'r' ? 24 : -1;
    }
    result.length = Math.abs(from - to) / ds[0];
    result.fill(ds[0]);
    return result;
  }
  const np = p === 'r' ? 'b' : 'r';
  const spots = state.spots;
  for (const d of ds) {
    const i = p === 'r' ? from + d : from - d;
    if (i < 0) continue;
    if (i > 23) continue;
    if (p === 'r' && i > to) continue;
    if (p === 'b' && i < to) continue;
    if (spots[i].pieces.length > 1 && spots[i].pieces[0] === np) continue;
    if (i === to) {
      return [d];
    } else {
      const rest = [...ds];
      rest.splice(ds.indexOf(d), 1);
      const used = getDiceUsed(state, p, i, to, rest);
      if (used.length) return [d, ...used];
    }
  }
  return [];
}

// Apply a move to the game state, returning a new state
function applyMove(state: GameState, p: Piece, from: number, to: number): GameState {
  const newState = cloneDeep(state);
  if (from === to) return newState;
  const dice = getDiceUsed(newState, p, from, to, getDice(newState, p));
  newState.diceUsed.push(...dice);
  const previous = from < 0 ? newState.bar : newState.spots[from].pieces;
  previous.splice(previous.findIndex(c => c === p), 1);
  let hit = false;
  let path = from < 0 ? (p === 'r' ? -1 : 24) : from;
  for (const d of dice.map(d => p === 'r' ? d : -d)) {
    path += d;
    if (path < 0 || path > 23) break;
    const hop = newState.spots[path].pieces;
    if (hop.length === 1 && hop[0] !== p) {
      if (hit) {
        newState.board.push(renderMove(p, from, path, hit));
        from = path;
      }
      hit = true;
      newState.bar.push(p === 'r' ? 'b' : 'r');
      hop.length = 0;
    }
  }
  const current = to < 0 ? undefined : newState.spots[to].pieces;
  if (!current?.length || current[0] === p) {
    current?.push(p);
    // If piece is going off, add to off collection
    if (to < 0) {
      if (p === 'r') {
        newState.redOff.push(p);
      } else {
        newState.blackOff.push(p);
      }
    }
    newState.board.push(renderMove(p, from, to, hit));
  } else if (current.length === 1) {
    newState.bar.push(current[0]);
    current[0] = p;
    newState.board.push(renderMove(p, from, to, hit));
  }
  // Update glow tracking
  if (to >= 0 && to < 24) {
    newState.lastMovedSpots[to] = (newState.lastMovedSpots[to] || 0) + 1;
  } else if (to < 0) {
    newState.lastMovedOff[newState.turn!]++;
  }
  if (from >= 0 && from < 24) {
    newState.lastMovedSpots[from] = (newState.lastMovedSpots[from] || 1) - 1;
  } else if (from < 0) {
    newState.lastMovedOff[newState.turn!]++;
  }
  // Check for winner
  if (!getRedPips(newState)) {
    newState.winner = 'r';
  } else if (!getBlackPips(newState)) {
    newState.winner = 'b';
  }
  newState.moves = getAllMoves(newState);
  return newState;
}

// Apply a dice roll to the game state
function applyRoll(state: GameState, p: Piece, d1: number, d2: number): GameState {
  const newState = {
    ...cloneDeep(state),
    diceUsed: [],
    lastMovedSpots: { },
    lastMovedOff: { 'r': 0, 'b': 0 },
  };
  const ds = p === 'r' ? newState.redDice : newState.blackDice;
  ds[0] = d1;
  ds[1] = d2;
  newState.board.push(`${p} ${d1}-${d2}`);
  if (!newState.turn && newState.redDice[0] && newState.blackDice[0]) {
    if (newState.redDice[0] === newState.blackDice[0]) {
      newState.redDice = [];
      newState.blackDice = [];
    } else {
      newState.turn = newState.redDice[0] > newState.blackDice[0] ? 'r' : 'b';
    }
  } else if (newState.turn) {
    newState.turn = p;
  }
  newState.moves = getAllMoves(newState);
  return newState;
}

function load(state: GameState, moves?: string[]) {
  if (!moves) return;
  for (const m of moves) {
    const parts = m.split(/[\s/*()]+/g).filter(p => !!p);
    const p = parts[0] as Piece;
    if (m.includes('-')) {
      // Clear glow on new roll
      state.lastMovedSpots = {};
      state.lastMovedOff = { 'r': 0, 'b': 0 };
      if (state.winner) throw $localize`Game Over`;
      if (state.turn && !isFirstRoll(state) && state.turn === p) throw $localize`Not your turn`;
      if (!isFirstRoll(state) && state.moves.length) throw $localize`Must move`;
      const ds = p === 'r' ? state.redDice : state.blackDice;
      if (!state.turn && ds[0]) throw $localize`Not your turn`;
      ds[0] = parseInt(m[2]);
      ds[1] = parseInt(m[4]);
      state.board.push(`${p} ${ds[0]}-${ds[1]}`);
      state.diceUsed = [];
      if (!state.turn && state.redDice[0] && state.blackDice[0]) {
        if (state.redDice[0] === state.blackDice[0]) {
          state.redDice = [];
          state.blackDice = [];
        } else {
          state.turn = state.redDice[0] > state.blackDice[0] ? 'r' : 'b';
        }
      } else if (state.turn) {
        state.turn = p;
      }
      state.moves = getAllMoves(state);
    } else {
      const bar = m.includes('bar');
      const off = m.includes('off');
      const from = bar ? 0 : parseInt(parts[1]);
      const to = off ? -1 : parseInt(parts[2]);
      const multiple = parseInt(parts[3] || '1');
      for (let i = 0; i < multiple; i++) {
        loadMove(state, p, from - 1, to - 1);
        // Track glow for moves when loading directly (not through animation system)
        const actualTo = to - 1;
        if (actualTo >= 0 && actualTo < 24) {
          state.lastMovedSpots[actualTo] = (state.lastMovedSpots[actualTo] || 0) + 1;
        } else if (actualTo === -2) {
          state.lastMovedOff[p]++;
        }
      }
    }
  }
  if (!getRedPips(state)) {
    state.winner = 'r';
  } else if (!getBlackPips(state)) {
    state.winner = 'b';
  }
}

function loadMove(state: GameState, p: Piece, from: number, to: number) {
  if (from === to) return;
  if (!state.moves[from]?.includes(to)) {
    throw $localize`Illegal move ${renderMove(p, from, to)}`;
  }
  const dice = getDiceUsed(state, p, from, to, getDice(state, p));
  state.diceUsed.push(...dice);
  const previous = from < 0 ? state.bar : state.spots[from].pieces;
  previous.splice(previous.findIndex(c => c === p), 1);
  let hit = false;
  let path = from < 0 ? (p === 'r' ? -1 : 24) : from;
  for (const d of dice.map(d => p === 'r' ? d : -d)) {
    path += d;
    if (path < 0 || path > 23) break;
    const hop = state.spots[path].pieces;
    if (hop.length === 1 && hop[0] !== p) {
      if (hit) {
        state.board.push(renderMove(p, from, path, hit));
        from = path;
      }
      hit = true;
      state.bar.push(p === 'r' ? 'b' : 'r');
      hop.length = 0;
    }
  }
  const current = to < 0 ? undefined : state.spots[to].pieces;
  if (!current?.length || current[0] === p) {
    current?.push(p);
    // If piece is going off, add to off collection
    if (to < 0) {
      if (p === 'r') {
        state.redOff.push(p);
      } else {
        state.blackOff.push(p);
      }
    }
    state.board.push(renderMove(p, from, to, hit));
  } else if (current.length === 1) {
    state.bar.push(current[0]);
    current[0] = p;
    state.board.push(renderMove(p, from, to, hit));
  }
  state.moves = getAllMoves(state);
  if (to >= 0 && to < 24) {
    state.lastMovedSpots[to] = (state.lastMovedSpots[to] || 0) + 1;
  } else if (to < 0) {
    state.lastMovedOff[state.turn!]++;
  }
  if (from >= 0 && from < 24) {
    state.lastMovedSpots[from] = (state.lastMovedSpots[from] || 1) - 1;
  } else if (from < 0) {
    state.lastMovedOff[state.turn!]++;
  }
}

@Component({
  selector: 'app-backgammon',
  templateUrl: './backgammon.component.html',
  styleUrls: ['./backgammon.component.scss'],
  hostDirectives: [CdkDropListGroup],
  host: {
    'class': 'backgammon-board',
    '[class.red]': 'red()',
    '[class.loaded]': 'loaded()',
    '[class.resizing]': 'resizing()',
    '[class.replay-mode]': 'replayMode()',
    '(window:resize)': 'onResize()',
  },
  imports: [CdkDropList, CdkDrag]
})
export class BackgammonComponent {
  private store = inject(Store);
  private actions = inject(ActionService);
  private el = inject<ElementRef<HTMLDivElement>>(ElementRef);


  // TODO: Save in local storage
  readonly red = model(false);
  readonly ref = input<Ref>();
  readonly text = input<string | undefined>('');
  readonly comment = output<string>();
  readonly copied = output<string>();

  readonly state = signal<GameState>(createInitialGameState());
  readonly moveRedOff = computed(() => this.lastState().turn === 'r' && this.selectedMoves().includes(-2));
  readonly moveBlackOff = computed(() => this.lastState().turn === 'b' && this.selectedMoves().includes(-2));
  readonly start = signal<number | undefined>(undefined);
  private readonly selectedMoves = computed(() => {
    const start = this.start();
    return start === undefined ? [] : this.lastState().moves[start] || [];
  });
  readonly rolling = signal<Piece | undefined>(undefined);
  readonly dragSource = signal(-1);
  readonly loaded = signal(false);
  readonly resizing = signal(false);
  private resizeTimer = 0;
  readonly replayMode = signal(false);
  readonly translate = signal<number | undefined>(undefined);
  readonly animating = signal(false);
  readonly animationQueue = signal<AnimationState[]>([]);
  readonly animatedPiece = signal<AnimationState | undefined>(undefined);

  readonly replayPosition = signal(0);
  readonly replayPlaying = signal(false);
  readonly replaySpeed = signal(1); // 1x, 2x, 3x, 4x
  readonly replayAnimations = signal<AnimationState[]>([]);
  readonly importantEventTypes = computed<ReadonlyMap<number, string>>(() => this.detectImportantEvents());
  readonly importantEvents = computed(() => [...this.importantEventTypes().keys()].sort((a, b) => a - b));

  private resizeObserver = window.ResizeObserver && new ResizeObserver(() => this.onResize()) || undefined;
  private watch?: Subscription;
  private append$!: (value: string) => Observable<string>;
  private reset$!: (value: string[]) => Observable<string>;
  private seeking = false;
  private animationHandler = 0;
  readonly errored = signal(false);

  constructor() {
    this.store.eventBus.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event.event === 'flip' && event.ref?.url === this.ref()?.url) {
        this.red.update(red => !red);
        defer(() => this.store.eventBus.fire('flip-done'));
      }
    });
    let first = true;
    let prevRef: Ref | undefined;
    effect(() => {
      const ref = this.ref();
      const text = this.text();
      untracked(() => {
        const refChanged = first || ref !== prevRef;
        const newRef = first || refChanged && (prevRef?.url !== ref?.url || prevRef?.origin !== ref?.origin);
        if (!ref || newRef) {
          this.watch?.unsubscribe();
          this.watch = undefined;
          if (ref || text != null) this.init();
        } else if (refChanged) {
          // Check if end game tags were added
          const prevEnded = !!(prevRef && (
            hasTag('plugin/backgammon/draw', prevRef) ||
            hasTag('plugin/backgammon/winner/r', prevRef) ||
            hasTag('plugin/backgammon/winner/b', prevRef)
          ));
          const nowEnded = this.isGameEnded();

          if (!prevEnded && nowEnded && !this.replayMode()) {
            // Tags were added to end the game, enter replay mode
            defer(() => this.enterReplayMode());
          }
        }
        first = false;
        prevRef = ref;
      });
    });
  }

  private readonly onInit = afterNextRender(() => {
    this.resizeObserver?.observe(this.el.nativeElement.parentElement!);
    this.onResize();
  });

  init() {
    this.el.nativeElement.style.setProperty('--red-name', '"🔴️ ' + (this.bgConf()?.redName || $localize`Red`) + '"');
    this.el.nativeElement.style.setProperty('--black-name', '"⚫️ ' + (this.bgConf()?.blackName || $localize`Black`) + '"');
    const ref = this.ref();
    if (!this.watch && ref) {
      const watch = this.actions.append(ref);
      this.append$ = watch.append$;
      this.reset$ = watch.reset$;
      this.watch = watch.updates$.pipe(
        catchError(err => {
          if (err.url) {
            // Game history rewritten
            alert($localize`Game History Rewritten!\n\nPlease Reload.`);
          }
          return of();
        }),
      ).subscribe(update => {
        const state = getAnimation(this.lastState(), update);
        if (state) {
          this.queueAnimation(state);
          // Check if game ended via update (e.g., tags added remotely)
          if (this.isGameEnded() && !this.replayMode()) {
            defer(() => this.enterReplayMode());
          }
        }
      });
    }
    this.reset(this.ref()?.comment || this.text());
  }

  private readonly afterViewInit = afterNextRender(() => this.loaded.set(true));

  private readonly onDestroy = inject(DestroyRef).onDestroy(() => {
    this.resizeObserver?.disconnect();
    clearTimeout(this.resizeTimer);
    this.watch?.unsubscribe();
    this.pauseReplay();
  });

  readonly bgConf = computed(() => {
    return this.ref()?.plugins?.['plugin/backgammon'];
  });

  readonly local = computed(() => {
    const ref = this.ref();
    return !ref?.created || ref.upload || ref?.origin === this.store.account.origin();
  });

  onResize() {
    const dim = Math.floor(hasTag('plugin/fullscreen', this.ref()) ? Math.min(
      screen.width / 28,
      screen.height / 26.5
    ) : Math.min(
      (innerWidth - 4) / 28,
      (this.el.nativeElement.parentElement?.offsetWidth || screen.width) / 28,
      (innerHeight - 20) / 26,
    ));
    const fontSize = Math.floor(1.5 * dim);
    this.el.nativeElement.style.setProperty('--dim', dim + 'px')
    this.el.nativeElement.style.setProperty('--piece-size', fontSize + 'px');
    clearTimeout(this.resizeTimer);
    this.resizing.set(true);
    this.resizeTimer = delay(() => this.resizing.set(false), 1000);
  }

  onDragStarted(index: number) {
    this.dragSource.set(index);
    if (index < 0) {
      this.onClickBar();
    } else {
      this.start.set(undefined);
      this.onClick(index);
    }
    const dim = Math.floor(this.el.nativeElement.offsetWidth / 24);
    const fontSize = Math.floor(1.5 * dim);
    document.body.style.setProperty('--drag-piece-size', fontSize + 'px');
  }

  reset(board = '') {
    const state = createInitialGameState();
    try {
      load(state, board.split('\n').map(m => m.trim()).filter(m => !!m));
    } catch (e) {
      console.error(e);
      this.errored.set(true);
    }
    this.state.set(state);
    if (this.isGameEnded()) defer(() => this.enterReplayMode());
  }

  readonly lastState = computed(() => this.animationQueue().at(-1)?.post || this.state());

  private queueState(state: GameState) {
    this.queueAnimation({ pre: this.lastState(), post: state });
  }

  drawPiece(p?: string) {
    if (!p) return '';
    return p === 'r' ? '🔴️' : '⚫️';
  }

  drop(event: CdkDragDrop<number, number, Piece>) {
    if (this.replayMode()) return; // Don't allow moves in replay mode
    const p = event.item.data;
    const from = event.previousContainer.data;
    const to = event.container.data;
    if (from === to) return;
    const state = this.lastState();
    if (!state.moves[from]?.includes(to)) {
      throw $localize`Illegal move ${renderMove(p, from, to)}`;
    }
    this.queueState(applyMove(state, p, from, to));
    this.clearMoves();
    this.save(p, from, to);
  }

  save(p: Piece, from: number, to: number, hit?: boolean) {
    const move = p + ' ' + (from < 0 ? 'bar' : from + 1) + '/' + (to < 0 ? 'off' : to + 1) + (hit ? '*' : '');
    if (this.errored()) {
      this.errored.set(false);
      this.reset$(this.state().board).subscribe();
    } else {
      this.append$(move).pipe(
        catchError(err => {
          if (err.status === 409) this.save(p, from, to, hit);
          return of();
        }),
      ).subscribe();
    }
  }

  check() {
    if (!getRedPips(this.state())) {
      this.state.update(s => ({ ...s, winner: 'r' }));
      // Enter replay mode when game ends during play
      if (!this.replayMode()) {
        defer(() => this.enterReplayMode());
      }
    } else if (!getBlackPips(this.state())) {
      this.state.update(s => ({ ...s, winner: 'b' }));
      // Enter replay mode when game ends during play
      if (!this.replayMode()) {
        defer(() => this.enterReplayMode());
      }
    }
    this.clearMoves();
  }

  clearMoves() {
    this.start.set(undefined);
  }

  onClick(index: number) {
    const state = this.lastState();
    const p = state.spots[index].pieces[0];
    const start = this.start();
    if (state.turn && start !== undefined && state.moves[start]?.includes(index)) {
      this.queueState(applyMove(state, state.turn, start, index));
      this.clearMoves();
      this.save(state.turn!, start, index);
      this.start.set(undefined);
      return;
    }
    if (index === this.start()) {
      this.start.set(undefined);
      return this.clearMoves();
    }
    this.start.set(undefined);
    if (p !== state.turn) return this.clearMoves();
    const move = state.moves[index];
    if (!move) return this.clearMoves();
    this.start.set(index);
  }

  onClickBar() {
    this.start.set(-1);
    const state = this.lastState();
    const move = state.moves[-1];
    if (!move) return this.clearMoves();
  }

  onClickOff() {
    const state = this.lastState();
    const start = this.start();
    if (start !== undefined && state.moves[start]?.includes(-2)) {
      this.queueState(applyMove(state, state.turn!, start, -2));
      this.clearMoves();
      this.save(state.turn!, start, -2);
      this.start.set(undefined);
    }
  }

  moveHighest(event: Event, index: number) {
    event.preventDefault();
    const state = this.lastState();
    if (!state.turn || !state.moves[index]) return;
    const ds = getDice(state, state.turn);
    let d = Math.max(ds[0], (ds[1] || 0));
    let to = state.turn === 'r' ? index + d : index - d;
    if (to < 0 || to > 23) to = -2;
    if (!state.moves[index].includes(to)) {
      d = Math.min(ds[0], (ds[1] || 7));
      to = state.turn === 'r' ? index + d : index - d;
      if (to < 0 || to > 23) to = -2;
      if (!state.moves[index].includes(to)) return;
    }
    this.queueState(applyMove(state, state.turn, index, to));
    this.clearMoves();
    this.save(state.turn, index, to);
  }

  moveBarHighest(event: Event) {
    const state = this.lastState();
    event.preventDefault();
    if (!state.turn || !state.moves[-1]) return;
    const ds = getDice(state, state.turn);
    let d = Math.max(ds[0], (ds[1] || 0));
    let to = state.turn === 'r' ? d - 1 : 24 - d;
    if (!state.moves[-1].includes(to)) {
      d = Math.min(ds[0], (ds[1] || 7));
      to = state.turn === 'r' ? d - 1 : 24 - d;
      if (!state.moves[-1].includes(to)) return;
    }
    this.queueState(applyMove(state, state.turn, -1, to));
    this.clearMoves();
    this.save(state.turn, -1, to);
  }

  readonly first = computed(() => {
    return isFirstRoll(this.state());
  });

  readonly moves = computed(() => {
    return this.state().moves;
  });

  readonly spots = computed(() => this.state().spots.map(spot => ({
    ...spot, move: this.selectedMoves().includes(spot.index),
  })));

  readonly turn = computed(() => {
    return this.state().turn;
  });

  readonly redDice = computed(() => {
    return this.state().redDice;
  });

  readonly blackDice = computed(() => {
    return this.state().blackDice;
  });

  readonly winner = computed(() => {
    return this.state().winner;
  });

  readonly blackBar = computed(() => {
    return getBlackBar(this.state());
  });

  readonly redBar = computed(() => {
    return getRedBar(this.state());
  });

  readonly redOff = computed(() => {
    return this.state().redOff;
  });

  readonly blackOff = computed(() => {
    return this.state().blackOff;
  });

  readonly blackPips = computed(() => {
    return getBlackPips(this.state());
  });

  readonly redPips = computed(() => {
    return getRedPips(this.state());
  });

  readonly lastMovedOff = computed(() => {
    return this.state().lastMovedOff;
  });

  readonly lastMovedSpots = computed(() => {
    return this.state().lastMovedSpots;
  });

  readonly gammon = computed(() => this.winner() === 'r' && this.blackPips() === 15
    || this.winner() === 'b' && this.redPips() === 15);

  readonly backgammon = computed(() => this.winner() === 'r' && this.blackPips() === 15 && (this.blackBar().length || isInRedHome(this.state()))
    || this.winner() === 'b' && this.redPips() === 15 && (this.redBar().length || isInBlackHome(this.state())));

  roll(p: Piece) {
    if (this.replayMode()) return; // Don't allow rolling in replay mode
    let state = this.lastState();
    if (state.winner) throw $localize`Game Over`;
    if (state.turn && !isFirstRoll(state) && state.turn === p) throw $localize`Not your turn`;
    if (!isFirstRoll(state) && state.moves.length) throw $localize`Must move`;
    const ds = p === 'r' ? state.redDice : state.blackDice;
    if (!state.turn && ds[0]) throw $localize`Not your turn`;
    this.rolling.set(p);
    delay(() => this.rolling.set(undefined), 750);
    state = applyRoll(this.lastState(), p, this.r(), state.turn ? this.r() : 0);
    if (this.errored()) {
      this.errored.set(false);
      this.reset$(this.state().board).subscribe();
    } else {
      this.append$(state.board[state.board.length - 1]).subscribe();
    }
    this.queueState(state);
  }

  r() {
    // TODO: Hash cursor
    return Math.floor(Math.random() * 6) + 1;
  }

  queueAnimation(state: AnimationState) {
    this.animationQueue.update(queue => [...queue, state]);
    if (!this.animating()) this.processAnimationQueue();
  }

  private setAnimationCoordinates(animation: AnimationState, duration?: number) {
    const fromSpot = animation.from === -1 ? null : animation.pre.spots.find(s => s.index === animation.from!);
    const toSpot = animation.to === -2 || animation.to === -1 ? null : animation.pre.spots.find(s => s.index === animation.to!);

    let fromCol = 0;
    let fromRow = 0;
    if (animation.from === -1) {
      fromCol = 7;
      fromRow = animation.piece === 'r' ? 1 : 0;
    } else if (fromSpot) {
      fromCol = fromSpot.col > 6 ? fromSpot.col + 1 : fromSpot.col;
      fromRow = fromSpot.top ? 0 : 1;
    }

    let toCol = 0;
    let toRow = 0;
    if (animation.to === -1) {
      toCol = 7;
      toRow = animation.piece === 'r' ? 1 : 0;
    } else if (animation.to === -2) {
      toCol = 0;
      toRow = animation.piece === 'r' ? 1 : 0;
    } else if (toSpot) {
      toCol = toSpot.col > 6 ? toSpot.col + 1 : toSpot.col;
      toRow = toSpot.top ? 0 : 1;
    }

    const stackOffset = (stackIndex: number | undefined, spot: Spot | null | undefined) => {
      let x = 0;
      let y = stackIndex || 0;
      if (stackIndex && stackIndex > 4) {
        x -= 0.05;
        y -= 5.2;
        if (stackIndex > 9) {
          x -= 0.05;
          y -= 5.2;
        }
      }
      if (spot && !spot.top) {
        x = -x;
        y = -y + 0.1;
      }
      return { x, y };
    };

    const fromOffset = stackOffset(animation.fromStackIndex, fromSpot);
    const toOffset = stackOffset(animation.toStackIndex, toSpot);
    this.el.nativeElement.style.setProperty('--xFrom', '' + (fromCol + fromOffset.x) * 2);
    this.el.nativeElement.style.setProperty('--yFrom', '' + (fromRow * 12 + fromOffset.y * 0.86) * 2);
    this.el.nativeElement.style.setProperty('--xTo', '' + (toCol + toOffset.x) * 2);
    this.el.nativeElement.style.setProperty('--yTo', '' + (toRow * 12 + toOffset.y * 0.86) * 2);
    if (duration !== undefined) {
      this.el.nativeElement.style.setProperty('--move-duration', duration + 'ms');
    }
  }

  processAnimationQueue() {
    this.animating.set(false);
    this.animatedPiece.set(undefined);
    this.rolling.set(undefined);
    if (this.animationQueue().length === 0) return;

    this.animating.set(true);
    const animation = this.animationQueue()[0];
    this.animationQueue.update(queue => queue.slice(1));

    // Handle rolling animation
    if (animation.rollingPiece) {
      this.state.set({ ...animation.post });
      this.rolling.set(animation.rollingPiece);
      delay(() => {
        // Check if game ended via tags after rolling animation
        if (this.isGameEnded() && !this.replayMode()) {
          defer(() => this.enterReplayMode());
        }
        this.processAnimationQueue();
      }, 750);
      return;
    }

    if (animation.from === undefined) {
      this.state.set({ ...animation.post });
      if (!this.state().diceUsed.length) {
        this.rolling.set(this.state().turn);
      }
      delay(() => {
        // Check if game ended via tags after state update
        if (this.isGameEnded() && !this.replayMode()) {
          defer(() => this.enterReplayMode());
        }
        this.processAnimationQueue();
      }, 750);
      return;
    }

    this.setAnimationCoordinates(animation);

    requestAnimationFrame(() => {
      this.animatedPiece.set(animation);
      const totalDuration = 1500;
      delay(() => {
        this.state.set({ ...animation.post });
        this.check();
        // Check if game ended via tags after animation completes
        if (this.isGameEnded() && !this.replayMode()) {
          defer(() => this.enterReplayMode());
        }
        this.processAnimationQueue();
      }, totalDuration);
    });
  }

  // Replay mode controls
  readonly isGameEnded = computed(() => {
    const ref = this.ref();
    return !!this.state().winner || hasTag('plugin/backgammon/draw', ref) ||
      hasTag('plugin/backgammon/winner/r', ref) ||
      hasTag('plugin/backgammon/winner/b', ref);
  });

  precomputeReplayAnimations() {
    const replayAnimations: AnimationState[] = [];

    // Start with initial state
    let currentState = createInitialGameState();

    // Build animation for each move
    for (const move of this.state().board) {
      const animation = getAnimation(currentState, move);
      if (animation) {
        replayAnimations.push(animation);
        currentState = animation.post;
      }
    }
    this.replayAnimations.set(replayAnimations);
  }

  detectImportantEvents() {
    const eventTypes = new Map<number, string>();

    let currentTurnStart = 0;
    let piecesHitThisTurn = 0;
    let redAllHome = false;
    let blackAllHome = false;

    for (let i = 0; i < this.replayAnimations().length; i++) {
      const animation = this.replayAnimations()[i];
      const move = animation.post.board.at(-1) || '';
      const parts = move.split(/[\s/*()]+/g).filter(p => !!p);
      const p = parts[0] as Piece;

      // Check for double 6s (rolls)
      if (animation.rollingPiece && move.includes('6-6')) {
        eventTypes.set(i, $localize`Double 6s`);
      }

      // Track turn changes
      if (animation.rollingPiece) {
        // New roll - check if we had >1 hit in previous turn
        if (piecesHitThisTurn > 1) {
          if (!eventTypes.has(currentTurnStart)) {
            eventTypes.set(currentTurnStart, $localize`${piecesHitThisTurn} pieces hit`);
          }
        }
        currentTurnStart = i;
        piecesHitThisTurn = 0;
      } else if (move.includes('*')) {
        // Piece was hit (bumped to bar) - indicated by asterisk
        piecesHitThisTurn++;
      }

      // Check if red or black all pieces are in home (using post state)
      const state = animation.post;
      if (!redAllHome && p === 'r') {
        let inHome = true;
        for (let j = 0; j < 18; j++) {
          if (state.spots[j].pieces.find(piece => piece === 'r')) {
            inHome = false;
            break;
          }
        }
        if (state.bar.find(b => b === 'r')) inHome = false;
        if (inHome && state.redOff.length < 15) {
          redAllHome = true;
          eventTypes.set(i, $localize`Red all home`);
        }
      }

      if (!blackAllHome && p === 'b') {
        let inHome = true;
        for (let j = 6; j < 24; j++) {
          if (state.spots[j].pieces.find(piece => piece === 'b')) {
            inHome = false;
            break;
          }
        }
        if (state.bar.find(b => b === 'b')) inHome = false;
        if (inHome && state.blackOff.length < 15) {
          blackAllHome = true;
          eventTypes.set(i, $localize`Black all home`);
        }
      }
    }

    // Check last turn
    if (piecesHitThisTurn > 1) {
      if (!eventTypes.has(currentTurnStart)) {
        eventTypes.set(currentTurnStart, $localize`${piecesHitThisTurn} pieces hit`);
      }
    }

    return eventTypes;
  }

  enterReplayMode() {
    if (!this.isGameEnded()) return;

    this.replayMode.set(true);
    this.replayPosition.set(0);
    this.precomputeReplayAnimations();
    this.replayToPosition(this.replayAnimations().length + 1);
  }

  replayToPosition(position: number | string) {
    if (this.replayPlaying()) throw 'must pause before seeking';
    const pos = typeof position === 'string' ? parseFloat(position) : position;
    if (isNaN(pos) || pos < 0) return;
    this.animatedPiece.set(undefined);
    this.rolling.set(undefined);
    if (!this.replayAnimations().length) {
      this.replayPosition.set(0);
      return;
    }

    if (pos >= this.replayAnimations().length) {
      this.replayPosition.set(this.replayAnimations().length + 1);
      this.state.set(this.replayAnimations()[this.replayAnimations().length - 1].post);
    } else {
      this.replayPosition.set(pos);
      this.state.set(this.replayAnimations()[pos].pre);
    }
  }

  playReplay() {
    if (this.replayPlaying()) return;
    this.replayPlaying.set(true);
    if (this.replayPosition() >= this.replayAnimations().length - 1) this.replayPosition.set(0);
    this.processReplayAnimationQueue();
  }

  processReplayAnimationQueue() {
    this.animatedPiece.set(undefined);
    this.rolling.set(undefined);
    if (!this.replayPlaying()) return;

    if (this.replayAnimations().length === 0) {
      this.replayPlaying.set(false);
      this.replayPosition.set(0);
      return;
    }

    if (this.replayPosition() > this.replayAnimations().length) this.replayPosition.set(0);
    const animation = this.replayAnimations()[this.replayPosition()];

    // Handle rolling animation
    if (animation.rollingPiece) {
      this.state.set({ ...animation.post });
      this.rolling.set(animation.rollingPiece);
      const duration = 750 / this.replaySpeed();
      delay(() => {
        this.replayPosition.update(p => p + 1);
        this.processReplayAnimationQueue();
      }, duration);
      return;
    }

    const totalDuration = 1500 / this.replaySpeed();
    this.setAnimationCoordinates(animation, totalDuration);

    requestAnimationFrame(() => {
      this.animatedPiece.set(animation);
      this.animationHandler = delay(() => {
        if (this.replayPlaying()) {
          this.state.set({ ...animation.post });
          if (this.replayPosition() === this.replayAnimations().length - 1) {
            // Don't loop
            this.pauseReplay();
          }
          this.replayPosition.update(p => p + 1);
          this.processReplayAnimationQueue();
        }
      }, totalDuration);
    });
  }

  pauseReplay() {
    if (this.animationHandler) {
      clearTimeout(this.animationHandler);
      this.animationHandler = 0;
    }
    this.replayPlaying.set(false);
    this.animatedPiece.set(undefined);
    this.rolling.set(undefined);
  }

  startSeeking() {
    if (this.replayPlaying()) {
      this.seeking = true;
      this.pauseReplay();
    }
  }

  seek(position: number | string) {
    this.pauseReplay();
    this.replayToPosition(position);
  }

  endSeeking() {
    if (this.seeking) {
      this.seeking = false;
      this.playReplay();
    }
  }

  snapToEvent(eventPos: number) {
    if (this.replayPlaying()) {
      this.pauseReplay();
      this.replayToPosition(eventPos);
      this.playReplay();
    } else {
      this.replayToPosition(eventPos);
    }
  }

  getEventEmoji(eventPos: number): string {
    const eventType = this.importantEventTypes().get(eventPos);
    if (!eventType) return '';

    if (eventType.includes('Double 6s')) return '🎲️ 🎲️';
    if (eventType.includes('Red all home')) return '🔴️ 🏠️';
    if (eventType.includes('Black all home')) return '⚫️ 🏠️';
    if (eventType.includes('hit')) return ['2️⃣️', '3️⃣️', '4️⃣️'][parseInt(eventType.split(' ')[0]) - 2] + ' ❌️';
    return '';
  }

  replayFastForward() {
    // Cycle through speeds: 1x -> 2x -> 3x -> 4x -> 1x
    this.replaySpeed.update(speed => speed >= 4 ? 1 : speed + 1);
    if (this.replayPlaying()) {
      this.pauseReplay();
      this.playReplay();
    }
  }

  floor(n: number) {
    return Math.floor(n);
  }
}
